import { useEffect, useState } from 'react';
import { onValue, ref, query, orderByChild, startAt } from 'firebase/database';
import { rtdb } from '../firebase';

/**
 * Live state for a single CROUS:
 *  - the CROUS object itself (`data`)
 *  - last 15 min of pings for live affluence
 *  - computed level (0 empty, 1 moderate, 2 full) + sample count
 *
 * Mirrors the mobile app's weighted-average formula roughly. For the
 * admin panel we don't need perfect parity — we just need a reasonable
 * live reading.
 */

const WINDOW_MS = 15 * 60 * 1000;

function computeLevel(pings) {
  if (!pings || pings.length === 0) return { level: null, count: 0 };
  const now = Date.now();
  let num = 0;
  let den = 0;
  for (const p of pings) {
    if (!p || typeof p.level !== 'number' || typeof p.timestamp !== 'number') continue;
    const age = now - p.timestamp;
    if (age < 0 || age > WINDOW_MS) continue;
    const fresh = 1 - age / WINDOW_MS;
    const weight = fresh * (typeof p.trustWeight === 'number' ? p.trustWeight : 1);
    num += p.level * weight;
    den += weight;
  }
  if (den === 0) return { level: null, count: pings.length };
  return { level: num / den, count: pings.length };
}

export default function useCrousLive(crousId) {
  const [data, setData] = useState(null);
  const [pings, setPings] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!crousId) return undefined;
    setLoading(true);
    const unsubCrous = onValue(ref(rtdb, `crous/${crousId}`), (snap) => {
      setData(snap.val());
      setLoading(false);
    });

    const since = Date.now() - WINDOW_MS;
    const q = query(ref(rtdb, 'pings'), orderByChild('timestamp'), startAt(since));
    const unsubPings = onValue(q, (snap) => {
      const rows = [];
      snap.forEach((c) => {
        const v = c.val();
        if (v && v.crousId === crousId) rows.push(v);
      });
      setPings(rows);
    });

    return () => {
      unsubCrous();
      unsubPings();
    };
  }, [crousId]);

  const { level, count } = computeLevel(pings);
  return { data, pings, level, count, loading };
}
